import { DataTable } from "@bcc/components/data-table/data-table";
import type { InvoiceStatus, PurchaseOrderInvoice } from "@shared/types/payout";
import { CellContext, ColumnDef, ColumnFiltersState, FilterFn } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { setInvoiceTableContainerTestId } from "./invoice-table.utils";
import {
  InvoiceActionsCell,
  InvoiceAmountCell,
  InvoiceDateCell,
  InvoiceDueDateCell,
  InvoiceHeaderCell,
  InvoiceIdCell,
  InvoiceOrdersCell,
  InvoiceStatusCell,
  InvoiceVatAmountCell,
  InvoiceVatRateCell,
} from "./invoice-table/cells";

interface InvoiceTableProps {
  invoices: PurchaseOrderInvoice[];
  onInvoiceClick: (invoice: PurchaseOrderInvoice) => void;
  isLoading?: boolean;
  testId?: string;
  columnFilters?: ColumnFiltersState;
  globalFilter?: string;
  exportToCSV?: boolean;
}

const getPurchaseOrderIdsString = (row: PurchaseOrderInvoice): string => row.invoiceLines.map((l) => l.purchaseOrderId).join(" ");

const createInvoiceActionsCell = (onViewDetails: (invoice: PurchaseOrderInvoice) => void) =>
  function InvoiceActions(props: CellContext<PurchaseOrderInvoice, unknown>) {
    return <InvoiceActionsCell {...props} onViewDetails={onViewDetails} />;
  };

export function InvoiceTable({
  invoices,
  onInvoiceClick,
  isLoading = false,
  testId,
  columnFilters = [],
  globalFilter,
  exportToCSV,
}: Readonly<InvoiceTableProps>) {
  const { t } = useTranslation();

  const handleRowClick = useCallback(
    (row: { original: PurchaseOrderInvoice }) => {
      onInvoiceClick(row.original);
    },
    [onInvoiceClick]
  );

  // Custom filter function for status array filtering
  const statusArrayFilter: FilterFn<PurchaseOrderInvoice> = (row, columnId, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) {
      return true;
    }
    const status = row.getValue(columnId) as InvoiceStatus;
    return filterValue.includes(status);
  };

  // Define columns with reusable cells
  const columns: ColumnDef<PurchaseOrderInvoice>[] = useMemo(
    () => [
      {
        accessorKey: "invoiceId",
        header: InvoiceHeaderCell,
        cell: InvoiceIdCell,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.invoiceId"),
        },
      },
      {
        accessorKey: "invoiceAmount",
        header: InvoiceHeaderCell,
        cell: InvoiceAmountCell,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.invoiceAmount"),
        },
      },
      {
        id: "vatRate",
        header: InvoiceHeaderCell,
        cell: InvoiceVatRateCell,
        enableSorting: false,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.vatRate"),
        },
      },
      {
        id: "vatAmount",
        header: InvoiceHeaderCell,
        cell: InvoiceVatAmountCell,
        enableSorting: false,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.vatAmount"),
        },
      },
      {
        accessorKey: "invoiceDate",
        header: InvoiceHeaderCell,
        cell: InvoiceDateCell,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.invoiceDate"),
        },
      },
      {
        accessorKey: "invoiceStatus",
        header: InvoiceHeaderCell,
        cell: InvoiceStatusCell,
        filterFn: statusArrayFilter,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.invoiceStatus"),
        },
      },
      {
        accessorKey: "payDueDate",
        header: InvoiceHeaderCell,
        cell: InvoiceDueDateCell,
        enableSorting: false,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.payDueDate"),
        },
      },
      {
        accessorKey: "poCount",
        id: "totalOrders",
        header: InvoiceHeaderCell,
        cell: InvoiceOrdersCell,
        enableSorting: false,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.totalOrders"),
        },
      },
      // Hidden columns for CSV export only
      {
        accessorKey: "dueDate",
        header: InvoiceHeaderCell,
        enableHiding: true,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.dueDate"),
        },
      },
      {
        accessorKey: "lastSettleDate",
        id: "lastSettleDate",
        header: InvoiceHeaderCell,
        enableHiding: true,
        meta: {
          exportHeader: t("finance.invoiceTable.columns.payDate"),
        },
      },
      {
        id: "poNumbers",
        accessorFn: getPurchaseOrderIdsString,
        enableSorting: false,
      },
      {
        id: "action",
        header: InvoiceHeaderCell,
        cell: createInvoiceActionsCell(onInvoiceClick),
        enableSorting: false,
      },
    ],
    [onInvoiceClick, t]
  );

  return (
    <div data-testid={testId ?? setInvoiceTableContainerTestId()} className="**:data-[testid='DataTable-FilterBar']:hidden">
      <DataTable
        columns={columns}
        data={invoices}
        isLoading={isLoading}
        emptyMessage={t("finance.invoiceTable.noInvoices")}
        onRowClick={handleRowClick}
        columnFilters={columnFilters}
        globalFilter={globalFilter}
        columnVisibility={{
          dueDate: false,
          lastSettleDate: false,
          poNumbers: false,
        }}
        exportFilename="invoices"
        exportColumns={["invoiceId", "invoiceAmount", "invoiceDate", "invoiceStatus", "poCount", "dueDate", "lastSettleDate"]}
        exportToCSV={exportToCSV}
        exportable={true}
      />
    </div>
  );
}
